import z from "zod";

import {
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
  PASSWORD_PATTERN,
} from "../constants/app";
import type {
  IEmailFieldProps,
  IPasswordFieldProps,
  TOptionalStringProps,
  TPositiveIntegerStringProps,
  TRequiredStringProps,
} from "./types";

// NOTE: All builders accept pre-translated error message strings (not i18n keys).
// NOTE: This keeps the builders decoupled from i18n and aligned with Zod's API.

// NOTE: Base string schema with optional trim and maxLength.
const baseString = ({
  maxLength,
  maxLengthMessage,
  trim = true,
}: Pick<
  TOptionalStringProps,
  "maxLength" | "maxLengthMessage" | "trim"
> = {}): z.ZodString => {
  let schema = z.string();

  if (trim) {
    schema = schema.trim();
  }

  if (maxLength !== undefined && maxLengthMessage) {
    schema = schema.max(maxLength, maxLengthMessage);
  }

  return schema;
};

// NOTE: Required string field. Applies trim by default.
export const requiredString = ({
  requiredMessage,
  regex,
  regexMessage,
  ...base
}: TRequiredStringProps): z.ZodString => {
  let schema = baseString(base).min(1, requiredMessage);

  if (regex && regexMessage) {
    schema = schema.regex(regex, regexMessage);
  }

  return schema;
};

// NOTE: Optional trimmed string field. Regex only validates non-empty values.
export const optionalString = ({
  regex,
  regexMessage,
  ...base
}: TOptionalStringProps = {}): z.ZodOptional<z.ZodString> => {
  const schema = baseString(base);

  if (regex && regexMessage) {
    // NOTE: Skip regex for empty strings to preserve optional semantics.
    return schema
      .refine((val) => !val || regex.test(val), regexMessage)
      .optional();
  }

  return schema.optional();
};

// NOTE: Email field with trim and custom invalid message.
export const emailField = ({ invalidMessage }: IEmailFieldProps): z.ZodString =>
  z.string().trim().email({ message: invalidMessage });

// NOTE: Password field with full validation chain (min/max length + pattern).
export const passwordField = ({
  requiredMessage,
  minLengthMessage,
  maxLengthMessage,
  patternMessage,
}: IPasswordFieldProps): z.ZodString =>
  requiredString({
    requiredMessage,
    maxLength: MAX_PASSWORD_LENGTH,
    maxLengthMessage,
    regex: PASSWORD_PATTERN,
    regexMessage: patternMessage,
    trim: false,
  }).min(MIN_PASSWORD_LENGTH, minLengthMessage);

// NOTE: Positive integer as string (e.g. "123") with optional max value bound.
export const positiveIntegerString = ({
  requiredMessage,
  invalidMessage,
  maxValue,
  tooLargeMessage,
}: TPositiveIntegerStringProps): z.ZodString =>
  requiredString({
    requiredMessage,
    regex: /^[1-9]\d*$/,
    regexMessage: invalidMessage,
    trim: false,
  }).superRefine((val, ctx) => {
    if (maxValue !== undefined && Number(val) > maxValue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: tooLargeMessage ?? invalidMessage,
      });
    }
  });
